/**
 * Authentication Context for Bond Palantir
 *
 * Provides authentication state and methods for the entire application.
 * Uses AWS Cognito with OIDC (react-oidc-context) for authentication.
 *
 * Features:
 * - Integrates with react-oidc-context for Cognito authentication
 * - Provides user information (email, name, from ID token claims)
 * - Handles login/logout via Cognito hosted UI
 * - Provides token access for API and WebSocket connections
 * - Fetches additional user data from backend API after authentication
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import config from '../config';

const AuthContext = createContext(null);

/**
 * Hook to access authentication context
 * @returns {Object} Authentication context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 *
 * Wraps the OIDC authentication with additional Bond-specific logic.
 * This provider should be used INSIDE the OIDC AuthProvider.
 */
export const AuthProvider = ({ children }) => {
  const oidcAuth = useOidcAuth();
  const [bondUser, setBondUser] = useState(null);
  const [bondUserLoading, setBondUserLoading] = useState(false);
  const [bondUserError, setBondUserError] = useState(null);
  const [initialCheckComplete, setInitialCheckComplete] = useState(false);

  /**
   * Fetch additional user data from Bond API after OIDC authentication
   */
  const fetchBondUserData = useCallback(async () => {
    if (!oidcAuth.isAuthenticated || !oidcAuth.user?.id_token) {
      setBondUser(null);
      return;
    }

    try {
      setBondUserLoading(true);
      setBondUserError(null);

      // Use ID token for user info (contains email, name, etc.)
      const response = await fetch(`${config.api.baseUrl}/api/auth/me/`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${oidcAuth.user.id_token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setBondUser(userData);
      } else if (response.status === 401 || response.status === 403) {
        // Token might be invalid, try to refresh
        console.warn('Bond user data fetch failed with auth error');
        setBondUser(null);
      } else {
        throw new Error(`Failed to fetch user data: ${response.status}`);
      }
    } catch (err) {
      console.error('Failed to fetch Bond user data:', err);
      setBondUserError(err.message);
      // Set minimal user data from OIDC claims
      setBondUser({
        email: oidcAuth.user?.profile?.email,
        name: oidcAuth.user?.profile?.name,
        is_admin: false,
        allowed_bonds: [],
      });
    } finally {
      setBondUserLoading(false);
    }
  }, [oidcAuth.isAuthenticated, oidcAuth.user]);

  /**
   * Track when initial OIDC check is complete
   */
  useEffect(() => {
    // Wait for OIDC library to finish initial loading
    if (!oidcAuth.isLoading && !initialCheckComplete) {
      setInitialCheckComplete(true);
    }
  }, [oidcAuth.isLoading, oidcAuth.isAuthenticated, oidcAuth.user, oidcAuth.error, initialCheckComplete]);

  /**
   * Fetch Bond user data when OIDC authentication completes
   */
  useEffect(() => {
    if (oidcAuth.isAuthenticated && oidcAuth.user) {
      fetchBondUserData();
    } else {
      setBondUser(null);
    }
  }, [oidcAuth.isAuthenticated, oidcAuth.user, fetchBondUserData]);

  /**
   * Login by redirecting to Cognito hosted UI
   */
  const login = useCallback(() => {
    oidcAuth.signinRedirect();
  }, [oidcAuth]);

  /**
   * Logout by signing out from OIDC and Cognito
   */
  const logout = useCallback(() => {
    // Custom Cognito logout to clear all sessions
    const clientId = config.auth.cognito.client_id;
    const logoutUri = config.auth.cognito.post_logout_redirect_uri;
    const cognitoDomain = config.auth.cognito.cognitoDomain;

    if (cognitoDomain && clientId && logoutUri) {
      // Sign out from OIDC client first
      oidcAuth.removeUser();
      // Then redirect to Cognito logout to clear Cognito session
      window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    } else {
      // Fallback to OIDC signout only
      oidcAuth.signoutRedirect();
    }
  }, [oidcAuth]);

  /**
   * Latest tokens kept in refs so getToken/getAccessToken can be STABLE callbacks.
   * react-oidc-context replaces `oidcAuth.user` on every silent token renew
   * (automaticSilentRenew). If these callbacks changed identity on each renew, every
   * effect that lists them as a dependency would re-run and silently reset state —
   * e.g. clearing the dashboard's live logs. Reading from a ref keeps the callback
   * identity stable while always returning the current token.
   */
  const idTokenRef = useRef(null);
  const accessTokenRef = useRef(null);
  idTokenRef.current = oidcAuth.user?.id_token || null;
  accessTokenRef.current = oidcAuth.user?.access_token || null;

  /**
   * Get ID token for API authentication
   * @returns {string|null} ID token or null if not available
   */
  const getToken = useCallback(() => idTokenRef.current, []);

  /**
   * Get access token for API authentication
   * @returns {string|null} Access token or null if not available
   */
  const getAccessToken = useCallback(() => accessTokenRef.current, []);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = oidcAuth.isAuthenticated;

  /**
   * Check if user is an admin
   */
  const isAdmin = bondUser?.is_admin || false;

  /**
   * Check if user has access to a specific bond
   * @param {string} bondId - Bond ID to check
   * @returns {boolean} True if user has access
   */
  const hasAccessToBond = useCallback((bondId) => {
    if (!bondUser) return false;
    if (bondUser.is_admin) return true;
    return bondUser.allowed_bonds?.includes(bondId) || false;
  }, [bondUser]);

  /**
   * Combined user object with OIDC and Bond data
   */
  const user = bondUser ? {
    ...bondUser,
    email: bondUser.email || oidcAuth.user?.profile?.email,
    name: bondUser.name || oidcAuth.user?.profile?.name,
    sub: oidcAuth.user?.profile?.sub,
    phone_number: oidcAuth.user?.profile?.phone_number,
  } : null;

  /**
   * Combined loading state
   */
  const loading = !initialCheckComplete || oidcAuth.isLoading || bondUserLoading;

  /**
   * Combined error state
   */
  const error = oidcAuth.error?.message || bondUserError;

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    getToken,
    getAccessToken,
    hasAccessToBond,
    checkAuth: fetchBondUserData, // Expose for manual refresh if needed
    // Expose OIDC auth for advanced use cases
    oidcAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Mock Authentication Provider — local development only.
 *
 * Provides the same context contract as AuthProvider but with a fake,
 * always-authenticated user and no OIDC/Cognito dependency, so the app can run
 * against the HAR mock server (src/web/mock-server/) fully offline.
 * Activated by index.js when REACT_APP_AUTH_MOCK=1 in a development build.
 */
export const MockAuthProvider = ({ children }) => {
  const noop = useCallback(() => {}, []);
  const getToken = useCallback(() => 'mock-id-token', []);
  const hasAccessToBond = useCallback(() => true, []);

  const value = {
    user: {
      email: 'mock@local.dev',
      name: 'Mock User',
      is_admin: true,
      allowed_bonds: [],
      sub: 'mock-sub',
    },
    loading: false,
    error: null,
    isAuthenticated: true,
    isAdmin: true,
    login: noop,
    logout: noop,
    getToken,
    getAccessToken: getToken,
    hasAccessToBond,
    checkAuth: noop,
    // Stub of the react-oidc-context surface ProtectedRoute reads
    oidcAuth: { isLoading: false, isAuthenticated: true, error: null },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
